/**
 * Authenticated app frame: sidebar on the left, scrolling content on the right,
 * plus the globally-mounted toast and app-level dialogs.
 *
 * The wrapper styles, the `lyx-main` / `lyx-content` classes and their max-width
 * clamp are verbatim from the prototype's root element.
 *
 * Routes render into <Outlet />. The chrome stays mounted across navigations,
 * which is what keeps the sidebar and the player from remounting on every route
 * change — and is also why this is the right place for the two app-wide effects:
 * they install once here and survive every navigation, where a route-level call
 * would tear the playback timer down and rebuild it on each page change.
 *
 * It is also the single owner of the sidebar's open/closed state: `useNavSidebar()` is
 * called exactly once, here, and the result goes down to the sidebar as props and out
 * to the Studio through context. Two call sites would be two subscriptions to the same
 * store — harmless — but also two places installing the Escape listener and the
 * close-on-navigate effect, which is not.
 *
 * AND IT OWNS THE MINI PLAYER, for the same reason it owns the two effects: the shell is
 * the only thing on screen that survives a route change, so a bar mounted here keeps its
 * position and its Pause button while the pages come and go beneath it. It is a view over
 * playerStore and not a second player — see the note at the mount, and MiniPlayer's own
 * header.
 *
 * AND IT DECIDES WHEN THE BAR ARRIVES, which is a rule about navigation and therefore
 * cannot live anywhere else: the shell is the one component that sees every route change
 * without being unmounted by one. The bar appears when a user carries playback off the
 * screen it was started on, and otherwise only when asked for. It deliberately does NOT
 * appear because something is playing — see `startedOn` below and uiStore's
 * `miniPlayerOpen`.
 *
 * AND — NEW — IT OWNS THE FULL-SCREEN PLAYER THE MINI PLAYER EXPANDS INTO. That view used
 * to be mounted only inside the Studio route, which is why the mini player's expand control
 * had to be a link into the Studio to reach anything at all. Mounted here it is reachable
 * from every route, so expanding the bar raises a player over the page you are on instead of
 * navigating you into the creation environment. The Studio keeps its own instance, wired to
 * the song that route is open on; both read the one playerStore, so they are two views and
 * not two players. See the mount.
 *
 * AND IT OWNS THE FALLBACK OPEN BUTTON. On drawer widths the sidebar is off-screen, so
 * without something on the page to summon it there would be no navigation at all — a
 * control disappearing with no replacement. The shell renders one, and a route that has
 * its own header toggle (the Studio) claims the job so there is never a second one. See
 * navSidebarContext.
 */
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, matchPath, useLocation } from 'react-router-dom';
import { ChevronUp, Menu } from 'lucide-react';
import LegalFooter from './LegalFooter';
import Sidebar from './Sidebar';
import { NavSidebarContext, NavToggleClaimContext } from './navSidebarContext';
import { LoadSkeleton, Toast, Tooltip } from '../ui';
import { AppModals } from '../modals';
import { FullscreenPlayer, MiniPlayer } from '../player';
import {
  useAppData,
  useNavSidebar,
  usePlayer,
  useSimulatedPlayback,
  useSyncUnlimited,
} from '../../hooks';
import { selectProfileName, useAuthStore } from '../../stores/authStore';
import { useUiStore } from '../../stores/uiStore';
import { paths } from '../../routes/paths';
import { c } from '../../styles/theme';
import type { Song } from '../../types/domain';

/**
 * The one route that already has a transport on screen: the Studio workspace.
 *
 * The mini player is suppressed there and nowhere else. Two floating transports for the
 * same song on one screen is the ambiguity the workspace's own layout rules out — its
 * ACTIVE SONG panel deliberately carries no play button so that the docked transport is
 * the only one — and a second bar in the corner would put a second Pause a thumb's width
 * from the first.
 *
 * `/studio` is NOT in this list. It is the resolver route: it renders no transport of its
 * own, it redirects onward, and the mini player belongs on it exactly as it belongs on
 * Settings.
 */
const WORKSPACE_PATTERN = '/library/:songId';
const FULL_HEIGHT_SCROLL_ROUTES = [paths.explore, paths.ticket];

/**
 * The Create section — where the bar does not raise itself, and waits to be asked.
 *
 * THE BATCH 6 §12 RULE: "The Mini Player should NOT appear inside the Create section
 * simply because audio is playing… Do not interrupt active creation workflows." These are
 * the four screens someone is *making* something on — the composer and the three other
 * ways in — and a floating panel arriving over a half-written prompt because a track from
 * the Library is still going is the interruption the brief names.
 *
 * SUPPRESSED IS NOT REMOVED, and that is the difference from the workspace above. The
 * workspace has its own transport for whatever is loaded, so the bar is simply not needed
 * there. Create has no transport for a track started anywhere else — its result card can
 * play the song it just generated (see routes/Create.tsx) and nothing on the page can touch
 * the one from the Library that is still going — so hiding the bar outright would leave a
 * playing song with no control on screen at all. Instead the shell renders a small reveal
 * control in the same corner, and pressing it shows the same bar — §12's "If the user
 * explicitly opens the Mini Player manually, allow it". Playback is untouched by any of this.
 *
 * Patterns rather than a string compare so a future child route (/create/:draftId, say)
 * is covered by the same rule its parent is.
 */
const CREATE_PATTERNS = [paths.create, paths.dna, paths.genreMixer, paths.imageMusic];

export default function AppShell() {
  // Exactly one call site each, per src/hooks/index.ts. Calling either twice
  // would double up its timer or effect.
  useSimulatedPlayback();
  useSyncUnlimited();
  /*
    AND THE APP'S READ ENDPOINTS, from the same place and for the same reason: the shell is
    what does not remount between routes, so this hydrates once rather than on every
    navigation. It is a no-op while the app runs on fixtures — which is every build today —
    and its own header explains why that is the correct behaviour rather than a stub. See
    hooks/useAppData.ts.
  */
  useAppData();

  const nav = useNavSidebar();
  const toast = useUiStore((s) => s.toast);
  const miniPlayerOpen = useUiStore((s) => s.miniPlayerOpen);
  const openMiniPlayer = useUiStore((s) => s.openMiniPlayer);
  const closeMiniPlayer = useUiStore((s) => s.closeMiniPlayer);
  const profileName = useAuthStore(selectProfileName);
  const { pathname } = useLocation();
  const mainRef = useRef<HTMLDivElement | null>(null);

  /*
    THE MINI PLAYER'S DATA, ALL OF IT, FROM THE ONE PLAYER.

    `usePlayer()` is a read/act view over playerStore — no clock, no position, no second
    copy of anything. The clock is the `useSimulatedPlayback()` call above, which is why
    playback already survived a navigation before this surface existed: the state is in
    the store and the shell does not remount. What was missing was any sign of it outside
    the workspace. So this adds a view, not a player.
  */
  const {
    songId,
    playingSong,
    paused,
    elapsed,
    loading,
    bufferedSec,
    durationLabel,
    volume,
    speed,
    repeat,
    muted,
    hasNext,
    hasPrevious,
    isActive,
    toggle,
    stopAndCloseMiniPlayer,
    playNext,
    playPrevious,
    seek,
    setVolume,
    setSpeed,
    toggleRepeat,
    toggleMute,
  } = usePlayer();
  const onWorkspace = matchPath(WORKSPACE_PATTERN, pathname) !== null;
  const onFullHeightScrollRoute = FULL_HEIGHT_SCROLL_ROUTES.some(
    (pattern) => matchPath(pattern, pathname) !== null
  );
  const onCreate = CREATE_PATTERNS.some((pattern) => matchPath(pattern, pathname) !== null);

  /*
    WHETHER THE EXPANDED PLAYER IS OPEN, and nothing else — local state, not the UI store.

    It is one boolean owned by one component and read by nothing else, which is the line
    uiStore's own header draws: the store is for state several places act on. The Studio's
    copy of this flag is local for the same reason, and the two cannot conflict because the
    mini player does not exist on the workspace route.

    The expanded view is closed rather than kept open when playback stops, by the
    `playingSong &&` guard on the mount: a full-screen player over an empty player is a
    dialog with nothing in it.
  */
  const [expanded, setExpanded] = useState(false);

  const openSongFullscreen = useCallback(
    (song: Song) => {
      if (!isActive(song.id)) toggle(song.id);
      setExpanded(true);
    },
    [isActive, toggle]
  );

  /*
    WHICH ROUTE THE CURRENT PLAYBACK SESSION WAS STARTED ON, and the effect that turns it
    into "the user has walked away from that screen". This is the mechanism the whole
    appearance rule rests on, and the one the brief singles out as what must NOT be written:
    not `if (isPlaying) showMiniPlayer()`, which is the version this replaced.

    A REF RATHER THAN STATE, because writing it must not itself cause a render — the effect
    below runs after every route change and after every change of song, and setting state
    unconditionally in it would be a loop. Nothing reads it during render either: it is a
    memory the effect compares against, and the visible outcome is `miniPlayerOpen` in the
    UI store.

    THE COMPARISON, in the effect, in order:

      - NOTHING LOADED. There is no session, so there is nothing to remember and nothing to
        show. The bar is closed, which is also how a session that ended on its own — the
        clock reaching the end of a queue, a delete, an explicit stop — leaves the flag: the
        next one starts closed rather than inheriting this one's answer.
      - A NEW SONG. `songId` differs from the one remembered, so this is a fresh session and
        the route it started on is the one we are on now. Recorded, and the bar is NOT
        opened: starting a track is not asking for the bar, and the surface that started it
        is right there on screen. It is not CLOSED either — an open bar follows the active
        song, because it is a view of the one player rather than a view of one track, and a
        bar that vanished when you picked the next song would be a transport that empties
        itself as you use it.
      - THE SAME SONG, A DIFFERENT ROUTE, STILL RUNNING. The user has carried playback off
        the screen it started on, which is the one gesture the bar exists for — nothing on
        the new page knows about the track. Open it, and move the remembered route forward.
      - THE SAME SONG, A DIFFERENT ROUTE, PAUSED. Nothing. A paused track is not playback
        being carried anywhere, and revealing the bar for one would be "the song was
        previously playing, so here is the bar" — the reading this whole effect exists to
        avoid. The route still moves forward, so resuming later and then leaving works
        normally. An ALREADY-OPEN bar is left open by this branch: pausing must not close
        the bar you are looking at, it must only fail to summon one.
      - THE SAME SONG, THE SAME ROUTE. A re-render, a pause, a resume, a seek. Nothing
        happens, which is what makes staying put on the Studio or the Library leave the
        screen alone however much you press.

    WHY THE ROUTE MOVES FORWARD every time. Without it, the remembered route would stay at
    the origin for the life of the session, every subsequent navigation would still read as
    "away from where it started", and closing the bar on the Settings screen would be undone
    by the next click in the sidebar — the reappearing-bar defect in a new form. Advancing it
    compares each navigation against the screen you were last on, so the bar opens once per
    departure and a close is respected until you leave a screen again.
  */
  const startedOn = useRef<{ songId: string; pathname: string } | null>(null);
  useEffect(() => {
    if (!songId) {
      startedOn.current = null;
      closeMiniPlayer();
      return;
    }
    const previous = startedOn.current;
    startedOn.current = { songId, pathname };
    if (!previous || previous.songId !== songId) return;
    if (previous.pathname !== pathname && !paused) openMiniPlayer();
  }, [songId, pathname, paused, openMiniPlayer, closeMiniPlayer]);

  /*
    WHETHER THE USER ASKED FOR THE BAR ON THE SCREEN THEY ARE ON — §12's manual reveal.

    Local state, for the reason `expanded` above is: one component owns it and nothing else
    reads it.

    AND IT IS CLEARED ON EVERY NAVIGATION, which is the part that needs the effect below and
    cannot be derived. A latch that survived the route change would mean one press while
    writing a prompt turned the suppression off for the rest of the session — including on
    the next Create screen, and for the next song the queue moves on to, which is exactly the
    "appears inside the Create section simply because audio is playing" §12 rules out.

    An effect rather than comparing a remembered pathname at render, because those are not
    the same rule and the difference is leaving and coming back: `revealedOn === pathname`
    would restore the panel on the second visit to /create, over a prompt that has since been
    submitted. Asking is a thing you do on a screen you are on, so walking away ends it.

    THE SHELL DOES NOT REMOUNT — that is the whole reason the bar keeps its position across
    routes — so there is nothing else that would reset this. `setState` with the same value
    is a bail-out in React, so the mount pass and every non-Create navigation cost nothing.

    It outranks the route suppression rather than replacing the dismissal: a bar the user
    has revealed can still be closed with its own X, because `dismissed` is checked
    alongside this and not instead of it.
  */
  const [createRevealed, setCreateRevealed] = useState(false);
  useEffect(() => {
    setCreateRevealed(false);
  }, [pathname]);

  /*
    THE ONE PREDICATE, so the bar and its reveal control cannot disagree.

    FOUR SEPARATE THINGS, deliberately not collapsed, because the brief's §6 is precisely
    about not collapsing them:

      `playingSong !== null`   there is a session to show. NOT a reason to show it.
      `miniPlayerOpen`         the user asked for the bar, or carried playback off the
                               screen it started on. This is the reason. See the effect
                               above and uiStore.
      `!onWorkspace`           the Studio already has a transport for the loaded song, so a
                               second floating one would put two Pause buttons a thumb's
                               width apart. A route rule, independent of the two above.
      `!suppressedOnCreate`    the Create screens keep it down until asked — §12.

    What is NOT in this expression is `!paused`, and its absence is the point: a bar that
    is up stays up through a pause, showing the position and a Resume control. Pausing is
    not a reason to show the bar (the effect above declines to open one) and not a reason
    to take it away either.

    `showMiniPlayer` mounts the bar; `offerCreateReveal` puts the small control in its
    place. One expression each, from the same parts, so they cannot both be on screen.
  */
  const suppressedOnCreate = onCreate && !createRevealed;
  const showMiniPlayer =
    playingSong !== null && miniPlayerOpen && !onWorkspace && !suppressedOnCreate;
  const offerCreateReveal = playingSong !== null && !onWorkspace && suppressedOnCreate;

  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;

    main.scrollLeft = 0;
    main.scrollTop = 0;
    requestAnimationFrame(() => {
      main.scrollLeft = 0;
      main.scrollTop = 0;
    });
  }, [pathname]);

  /*
    How many mounted routes are rendering their own nav toggle. A counter rather than a
    boolean because during a route transition the outgoing page can still be mounted
    while the incoming one registers, and a boolean would be set false by the departing
    component's cleanup after the arriving one had already claimed it — leaving two
    toggles on screen, or none.

    `claim` is wrapped in useCallback with no dependencies so it is referentially stable
    and the claiming effect runs on mount and unmount only.
  */
  const [claims, setClaims] = useState(0);
  const claim = useCallback(() => {
    setClaims((n) => n + 1);
    return () => setClaims((n) => n - 1);
  }, []);
  const showShellNavOpen = nav.drawer && claims === 0;

  return (
    <div
      style={{
        fontFamily: 'Inter, sans-serif',
        background: c.bg,
        color: c.text1,
        overflow: 'hidden',
        width: '100%',
        height: '100dvh',
        display: 'flex',
        position: 'relative',
      }}
    >
      {/*
        THE SIDEBAR'S FOUR PROPS, all from the one `useNavSidebar()` call above — the drawer's
        two and the minimise pair. Nothing here decides between them: the hook resolves
        `compact` to false in drawer mode, so the shell hands down a consistent pair and the
        sidebar renders what it is given. See useNavSidebar's `compact`.

        The main column needs no prop for any of this. `.lyx-main` is `flex: 1`, so the width
        the sidebar gives up is taken by the content on its own — which is why minimising is
        one class on one element rather than a layout change.
      */}
      <Sidebar
        drawer={nav.drawer}
        drawerOpen={nav.drawerOpen}
        onClose={nav.close}
        compact={nav.compact}
        onToggleCompact={nav.toggleCompact}
      />

      {/*
        The scrim behind the open drawer. It does two jobs that are easy to mistake for
        one: it dims the page so the drawer reads as being on top of it rather than part
        of it, and it makes tapping anywhere outside the menu close it — which is how
        every mobile drawer behaves and the only dismissal a first-time user will try.

        A div rather than a ::backdrop or a pseudo-element on the nav, because it has to
        be clickable and it has to sit between the page and the sidebar in the stack.
        Not a button: it has no name worth announcing and the drawer already has a real
        close button and Escape, so exposing it to assistive technology would add a
        second, nameless way to do the same thing.
      */}
      {nav.drawerOpen && (
        <div className="lyx-sidebar-overlay" onClick={nav.close} aria-hidden="true" />
      )}

      <NavSidebarContext.Provider value={nav}>
        <NavToggleClaimContext.Provider value={claim}>
          <div
            ref={mainRef}
            className={['lyx-main', showShellNavOpen ? 'lyx-main-with-nav-open' : '']
              .filter(Boolean)
              .join(' ')}
            style={{ position: 'relative' }}
          >
            <div className="lyx-content">
              {/*
                THE ROUTE CONTENT, through one shell-owned suspense boundary.

                The shell is the thing we are keeping eager in the first optimisation pass:
                the sidebar, player surfaces and route-level navigation affordances should
                appear immediately even while a heavier screen module is still on the wire.
                So the fallback lives HERE, around the Outlet and inside the shell, rather
                than higher in the tree where it would replace the chrome as well.

                `LoadSkeleton` rather than a blank gap, for the same reason the collection
                screens use it: the page is doing work, and a reader needs that fact stated.
                The footer stays outside the boundary so the shell keeps its own layout while
                the route module resolves.
              */}
              <Suspense fallback={<LoadSkeleton rows={3} label="Loading this page…" />}>
                <Outlet />
              </Suspense>
              {/*
                THE LEGAL FOOTER — §4's "linked from the footer", and the app's first
                footer of any kind.

                Inside .lyx-content and after the Outlet, so it lands at the end of
                whatever page is rendered and inherits the same reading column rather
                than needing a width of its own.

                NOT ON THE WORKSPACE. That route sets .lyx-content to `height: 100%` and
                fills it with a grid, so a row appended after it would push the transport
                out of the viewport — the one place in the app where the content column is
                not a scrolling document. `onWorkspace` is the same condition that
                suppresses the mini player, reused rather than recomputed.
              */}
              {!onWorkspace && !onFullHeightScrollRoute && <LegalFooter />}
            </div>
          </div>
        </NavToggleClaimContext.Provider>
      </NavSidebarContext.Provider>

      {/*
        The page's way back to the navigation, on the widths where the sidebar is
        not on screen.

        Outside `.lyx-main` now, for the same reason as the mini player and the dialogs:
        this button is fixed to the viewport, and inside the scroll container it would
        position against scrolled content — which is the mobile defect the user reported.

        The LOGIC is untouched. Same condition, same handler, same aria state, same claim
        system with the Studio's header button. Only its mount point and CSS positioning
        change.

        While the drawer IS open it stays mounted but goes inert — hidden from assistive
        technology and out of the tab order, because it is under the scrim and the drawer
        has its own close button. Mounted rather than removed so the drawer can hand focus
        back to it, which is the whole reason it cannot simply be conditioned away. Same
        arrangement as the workspace header's copy; see StudioWorkspace.
      */}
      {showShellNavOpen && (
        <Tooltip label="Open the menu" placement="bottom">
          <button
            type="button"
            onClick={nav.toggle}
            className="lyx-nav-open"
            aria-label="Open navigation menu"
            aria-expanded={nav.drawerOpen}
            aria-hidden={nav.drawerOpen || undefined}
            tabIndex={nav.drawerOpen ? -1 : undefined}
          >
            <Menu size={17} color={c.text1} />
          </button>
        </Tooltip>
      )}

      {/*
        THE MINI PLAYER — the app's "still playing" surface.

        Here, in the shell, because that is the whole point of it: the shell is what does
        not remount between routes, so the bar stays put and the position keeps counting
        while you walk from a playlist to Settings to the Library. Mounting it inside the
        routes would give every route its own, each one starting from scratch.

        Outside .lyx-main for the same reason the toast and the dialogs are: it is fixed to
        the viewport, and inside the scroll container it would be positioned against
        scrolled content.

        NOTHING PLAYING, NOTHING RENDERED — not a hidden or empty bar. `playingSong` is
        null both before anything has been started and after `stop()`, which is where the
        clock lands at the end of a queue, so the bar appears and leaves on its own.

        Prev and Next are passed only when the queue actually has somewhere to go, which
        is how the transport and the queue panel treat them too: for a single track they
        are absent rather than sitting there dead. `toggle(id)` on the loaded song flips
        only `paused` — position and queue survive it — so pausing here and resuming in
        the workspace is one continuous playback.

        CLOSING STOPS THE MUSIC, and that is the correction this mount carries. It used to
        hide the view and nothing else, which left a track running with no visible owner:
        the only surface that said what was playing had just been dismissed, so silencing
        it meant going to find the song again. `stopAndCloseMiniPlayer` does both halves —
        see usePlayer, where the pairing lives, since a UI store cannot reach the player.

        EXPANDING opens the full-screen player below rather than navigating — the control
        used to be a link into the Studio workspace, which took the user out of the page they
        were on and into the creation environment. See MiniPlayer's header.

        AND IT STAYS DOWN ON THE CREATE SCREENS until it is asked for — see
        CREATE_PATTERNS and the reveal control below. One `showMiniPlayer` predicate rather
        than a chain of `&&`s here, so the bar and that control are decided by the same
        expression and cannot both be on screen.
      */}
      {playingSong && showMiniPlayer && (
        <MiniPlayer
          song={playingSong}
          playing={!paused}
          elapsed={elapsed}
          bufferedSec={bufferedSec}
          totalLabel={durationLabel}
          loading={loading}
          speed={speed}
          onTogglePlay={() => toggle(playingSong.id)}
          onPrev={hasPrevious ? playPrevious : undefined}
          onNext={hasNext ? playNext : undefined}
          onSeek={(seconds) => seek(playingSong.id, seconds)}
          onExpand={() => setExpanded(true)}
          onClose={stopAndCloseMiniPlayer}
        />
      )}

      {/*
        THE REVEAL — §12's "If the user explicitly opens the Mini Player manually, allow it".

        A 30px button in the corner the bar itself would occupy, on the Create screens only,
        and only while something is actually playing. It is the answer to the obvious
        objection to suppressing the bar there: without it, a track started in the Library
        and still going while you write a prompt would have no control anywhere on screen,
        which is a worse defect than the interruption §12 is about.

        It names the song, so the reader knows what pressing it is about to show them, and it
        opens the bar as well as clearing the route suppression — both, because either alone
        is a dead control: the route latch without `openMiniPlayer` would lift the Create
        suppression over a bar that was never open, and `openMiniPlayer` without the latch
        would open a bar this route immediately suppresses.

        ONE OF THE TWO WAYS THE BAR IS ASKED FOR — the other is "Play in Mini Player" on a
        song row. Both are explicit; nothing else opens it except carrying playback off the
        screen it started on. See the effect above.

        Not a second player: it renders no transport of its own and holds no playback state.
        It flips one local boolean and the bar above mounts.

        Deliberately OUTSIDE .lyx-main, beside the bar it summons, for the same reason the
        bar is: it is fixed to the viewport, and the content column scrolls.
      */}
      {playingSong && offerCreateReveal && (
        <Tooltip label="Show the mini player" placement="top">
          <button
            type="button"
            className="lyx-mini-reveal"
            aria-label={`Show the mini player for ${playingSong.title}`}
            onClick={() => {
              openMiniPlayer();
              setCreateRevealed(true);
            }}
            style={{
              background: c.surface1,
              border: `1px solid ${c.borderStrong}`,
              color: c.text1,
              boxShadow: '0 6px 24px rgba(0,0,0,0.45), 0 1px 2px rgba(0,0,0,0.3)',
            }}
          >
            <ChevronUp size={15} aria-hidden="true" />
          </button>
        </Tooltip>
      )}

      {/*
        THE EXPANDED PLAYER — where the mini player's ↗ goes.

        Every control is the same `usePlayer()` action the mini player's and the Studio
        transport's equivalents call, so this is a third VIEW of one playerStore and not a
        third player: pause in here and the bar behind it shows paused at the same position.

        Not rendered on the workspace route, and not because it would misbehave — the Studio
        mounts its own instance for the song that route is open on, and two full-screen
        dialogs for one player would be two dialogs. `onWorkspace` is the same condition that
        suppresses the bar, for the same reason.

        `active={isActive(...)}` rather than defaulting to `playing`, so pausing holds the
        position instead of snapping the seek bar and the clock to zero — usePlayer's two
        predicates, applied the way the Studio applies them.
      */}
      {playingSong && !onWorkspace && (
        <FullscreenPlayer
          open={expanded}
          song={playingSong}
          artist={profileName}
          playing={!paused}
          active={isActive(playingSong.id)}
          elapsed={elapsed}
          bufferedSec={bufferedSec}
          totalLabel={durationLabel}
          loading={loading}
          volume={volume}
          speed={speed}
          repeat={repeat}
          muted={muted}
          onClose={() => setExpanded(false)}
          onTogglePlay={() => toggle(playingSong.id)}
          onSeek={(seconds) => seek(playingSong.id, seconds)}
          onSetVolume={setVolume}
          onSetSpeed={setSpeed}
          onToggleRepeat={toggleRepeat}
          onToggleMute={toggleMute}
          onPrev={hasPrevious ? playPrevious : undefined}
          onNext={hasNext ? playNext : undefined}
        />
      )}

      {/* Outside the scrolling container, so a dialog opened from a scrolled page
          is centred on the viewport rather than on the content. */}
      <AppModals onOpenFullscreen={openSongFullscreen} />
      <Toast message={toast} />
    </div>
  );
}
